import { FeatureFlags } from '../../feature-flags';

import type { NavigationEntry } from '@shared/navigation-sidebar';

/** Droits BackStage dont depend la visibilite du menu. */
export interface DroitsNavigation {
  readonly accesClients: boolean;
  readonly accesComptabilite: boolean;
  readonly featureFlags: Record<string, boolean>;
}

/**
 * Visibilite de chaque entree, par identifiant.
 *
 * Les NEUF identifiants de `NAVIGATION_BACKSTAGE` sont ecrits explicitement, y
 * compris les enfants : leur droit est pose ici plutot que deduit de la section.
 */
function autorisations(droits: DroitsNavigation): Record<string, boolean> {
  const { accesClients, accesComptabilite, featureFlags } = droits;
  return {
    clients: accesClients,
    redditions: accesComptabilite,
    evenements: accesComptabilite,
    formules: accesComptabilite && (featureFlags[FeatureFlags.REDDITION_FORMULE] ?? false),
    'fichier-sap': accesComptabilite,
    rapprochements: accesComptabilite,
    'rapprochement-manuel': accesComptabilite,
    parametrage: accesComptabilite,
    commissions: accesComptabilite,
  };
}

/**
 * Filtre `NAVIGATION_BACKSTAGE` selon les droits applicatifs.
 *
 * La barre du kit ne connait que les droits du Manager (`SECTION_RIGHTS`) et
 * n'applique aucun filtre a une route qu'elle ne repertorie pas : le filtrage
 * BackStage — roles Azure et drapeaux de fonctionnalite — se fait donc ici.
 *
 * **Defaut FERME.** Un identifiant absent d'`autorisations()` est MASQUE. Avec un
 * defaut ouvert, ajouter une entree sans penser a la table la rendait visible de
 * tous, silencieusement. Le menu n'est pas une frontiere de securite — les 9
 * routes sont derriere un `rolesGuard` — mais un oubli doit se voir tout de suite.
 *
 * Fonction pure et hors du composant pour que cette garantie soit testable sans
 * monter MSAL.
 */
export function filtrerNavigation(
  navigation: readonly NavigationEntry[],
  droits: DroitsNavigation,
): readonly NavigationEntry[] {
  const autorise = autorisations(droits);
  const visible = (id: string): boolean => autorise[id] ?? false;

  return navigation
    .filter(entree => visible(entree.id))
    .map(entree =>
      entree.kind === 'section' ? { ...entree, children: entree.children.filter(e => visible(e.id)) } : entree,
    );
}
